import { isEmpty } from 'lodash-es';
import { forwardRef, type PropsWithChildren, type ReactElement } from 'react';

import { HistogramChartLegend } from './components/legend/HistogramChartLegend.js';
import {
  DEFAULT_CHART_HEIGHT,
  DEFAULT_LOADING_STATE,
} from './constants/defaults.js';
import { HistogramLoadingContext } from './contexts/HistogramLoading.context.js';
import { HistogramChart as HistogramChartInternal } from './HistogramChart.js';
import { useHistogramChartConfig } from './hooks/useHistogramChartConfig.js';
import { HistogramConfigProvider } from './providers/HistogramConfigProvider.js';
import { HistogramDataProvider } from './providers/HistogramDataProvider.js';
import { HistogramFilteredDataProvider } from './providers/HistogramFilteredDataProvider.js';
import { HistogramStateSyncProvider } from './providers/HistogramStateSyncProvider.js';
import { StoreProvider } from './providers/StoreProvider.js';
import { Annotations } from './slots/annotations/Annotations.js';
import { Marker } from './slots/annotations/Marker.js';
import { Track } from './slots/annotations/Track.js';
import { Legend } from './slots/Legend.js';
import { Select } from './slots/Select.js';
import { Threshold } from './slots/Threshold.js';
import { Tooltip } from './slots/Tooltip.js';
import { XAxis } from './slots/XAxis.js';
import { YAxis } from './slots/YAxis.js';
import type { HistogramChartRef } from './types/histogram-ref.js';
import type { HistogramChartProps } from './types/histogram.js';
import { ChartLayout } from '../core/components/chart-layout/ChartLayout.js';
import { useAutoLegendRefresh } from '../core/components/chart-layout/hooks/useAutoLegendRefresh.js';
import { getChartStatesTemplates } from '../core/components/chart-states/utils/get-chart-states-template.js';
import { ChartSeriesActionContext } from '../core/components/series-action/ChartSeriesActionContext.js';
import { EmptyStateSlot } from '../core/slots/chart-states/EmptyStateSlot.js';
import { ErrorStateSlot } from '../core/slots/chart-states/ErrorStateSlot.js';
import { coerceSizeValue } from '../core/utils/coerceSizeValue.js';
import { AnnotationsTooltip } from './slots/annotations/Tooltip.js';
import { Intent } from '../../core/slots/Intent/Intent.js';

/**
 * @public
 */
export const HistogramChartRoot = forwardRef<
  HistogramChartRef,
  PropsWithChildren<HistogramChartProps>
>((props, forwardedRef) => {
  const {
    children,
    data,
    height,
    width,
    colorPalette,
    truncationMode,
    loading = DEFAULT_LOADING_STATE,
    seriesActions,
    initialZoom,
    currentZoom,
    onZoomChange,
    infiniteZoom,
    className,
    style,
    valueRepresentation,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
  } = props;
  const chartHeight = coerceSizeValue(height ?? DEFAULT_CHART_HEIGHT);
  const chartConfig = useHistogramChartConfig(children, {
    colorPalette,
    truncationMode,
    valueRepresentation,
    initialZoom,
    currentZoom,
    onZoomChange,
    infiniteZoom,
  });
  const { emptyState, errorState } = getChartStatesTemplates(props.children);

  const {
    position: legendPosition,
    onRatioChange,
    ratio: legendRatio,
    hidden: isLegendHidden,
  } = chartConfig.legend;

  const chartLayoutRef = useAutoLegendRefresh(data);

  return (
    <HistogramLoadingContext.Provider value={loading}>
      <HistogramConfigProvider config={chartConfig}>
        <HistogramDataProvider data={data}>
          <ChartSeriesActionContext.Provider
            value={{
              // We need a cast here as the provider expects unknown and the
              // react context will not be able to carry the generic anyways.
              chartSeriesActions: seriesActions as (
                series: unknown,
              ) => ReactElement<PropsWithChildren>,
            }}
          >
            <StoreProvider>
              <HistogramFilteredDataProvider>
                <HistogramStateSyncProvider
                  currentDomain={chartConfig.currentZoom as [number, number]}
                  initialDomain={chartConfig.initialZoom as [number, number]}
                  infiniteZoom={chartConfig.infiniteZoom}
                >
                  <ChartLayout
                    ref={chartLayoutRef}
                    chartHeight={chartHeight}
                    chartWidth={width}
                    isEmpty={isEmpty(data)}
                    emptyState={emptyState}
                    errorState={errorState}
                    showLoader={loading}
                    className={className}
                    style={style}
                    data-dtrum-mask={dataDtrumMask}
                    data-dtrum-allow={dataDtrumAllow}
                  >
                    <ChartLayout.Graph>
                      <HistogramChartInternal
                        ref={forwardedRef}
                        config={chartConfig}
                      />
                    </ChartLayout.Graph>
                    {!isLegendHidden && (
                      <ChartLayout.Legend
                        position={legendPosition}
                        ratio={legendRatio}
                        onResize={onRatioChange}
                      >
                        <HistogramChartLegend />
                      </ChartLayout.Legend>
                    )}
                  </ChartLayout>
                </HistogramStateSyncProvider>
              </HistogramFilteredDataProvider>
            </StoreProvider>
          </ChartSeriesActionContext.Provider>
        </HistogramDataProvider>
      </HistogramConfigProvider>
    </HistogramLoadingContext.Provider>
  );
});

(
  HistogramChartRoot as typeof HistogramChartRoot & { displayName: string }
).displayName = 'HistogramChartRoot';

/**
 * A `HistogramChart` is a type of graph that represents the distribution of numerical data.
 * It’s constructed by dividing the entire range of values into a series of intervals—known as bins
 * and then counting how many values fall into each interval.
 * The bins are typically of equal size and are adjacent to each other, with no gaps. Each bin is represented by a bar,
 * where the height of the bar reflects the frequency or count of data points within that interval
 * @public
 */
export const HistogramChart = Object.assign(HistogramChartRoot, {
  XAxis,
  YAxis,
  Legend,
  Tooltip,
  EmptyState: EmptyStateSlot,
  ErrorState: ErrorStateSlot,
  Annotations,
  Threshold,
  Select,
  Intent,
});

/**
 * Annotations Slots
 * @public
 */
export const HistogramAnnotations = Object.assign(
  {},
  {
    Marker,
    Track,
    Tooltip: AnnotationsTooltip,
  },
);
